// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CalendarTodayOutlined,
  ExpandMoreRounded,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { useFocusHighlight } from '@/hooks/use-focus-highlight';
import { ChargeRow } from '@/project/components/charge-row';
import { sumChargeTotals } from '@/project/utilities/charge-amounts';
import { ChargeFormValue } from '@/project/utilities/generate-charges';
import { pluralize } from '@/utilities/pluralize';
import { Box, ButtonBase, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

const COLLAPSE_THRESHOLD = 13;

interface ChargeWithIndex {
  charge: ChargeFormValue;
  index: number;
  // Label decorated with "(n/total)" when the charge is part of a split
  // sibling group. Computed in the list section where the full local
  // charge list is visible; passed down here unchanged so ChargeRow
  // doesn't need to see other rows to render its own label.
  displayLabel: string;
}

interface Props {
  dateKey: string;
  charges: ChargeWithIndex[];
  currencyCode: IsoCurrencyCode;
  /**
   * Milestone plans label the group "Expected bill date: …" because the
   * date is user-entered planning metadata, not a schedule the cron will
   * act on. Periodic plans use "Bill date: …".
   */
  isMilestoneMode: boolean;
  editingChargeIndex: number | null;
  focusChargeKey?: string;
  onFocusChargeComplete?: () => void;
  onStartEdit: (index: number) => void;
  onStopEdit: () => void;
  onUpdateCharge: (index: number, updated: ChargeFormValue) => void;
  onDeleteCharge: (index: number) => void;
  onSplitCharge: (index: number) => void;
}

export const ChargeBillDateGroup = memo(function ChargeBillDateGroup(
  props: Props
) {
  // Auto-expand large groups when the deep-link focus charge lives inside,
  // otherwise the row would never mount and the scroll/highlight could not
  // run. Recomputed each render via the initialState callback so changing
  // the focus key after mount also expands lazily — but if the user manually
  // collapses, that choice persists for non-focused groups.
  const containsFocusedCharge = useMemo(
    () =>
      props.focusChargeKey !== undefined &&
      props.charges.some((entry) => entry.charge.key === props.focusChargeKey),
    [props.charges, props.focusChargeKey]
  );

  const [isExpanded, setIsExpanded] = useState(
    () => props.charges.length < COLLAPSE_THRESHOLD || containsFocusedCharge
  );

  // Force-expand if a new focus key targets this group after mount.
  useEffect(() => {
    if (containsFocusedCharge) {
      setIsExpanded(true);
    }
  }, [containsFocusedCharge]);

  // Deep-link focus: scroll the entire group into view (only when out of
  // viewport) and flash the section background. Implementation lives in the
  // shared useFocusHighlight hook so other "jump to a thing" deep links can
  // reuse the exact same effect.
  const { ref: groupRef, isHighlighted } = useFocusHighlight<HTMLDivElement>({
    isFocused: containsFocusedCharge,
    onComplete: props.onFocusChargeComplete,
  });

  const handleExpand = useCallback(() => setIsExpanded(true), []);
  const handleCollapse = useCallback(() => setIsExpanded(false), []);

  const isAnotherEditing = props.editingChargeIndex !== null;

  // Wrapper styling shared by collapsed and expanded layouts so the highlight
  // covers the whole section regardless of which branch is rendered.
  const wrapperSx = {
    px: 0.5,
    mx: -0.5,
    borderRadius: 1,
    transition: 'background-color 600ms ease-out',
    backgroundColor: isHighlighted ? 'secondary.main' : 'transparent',
  } as const;

  // Collapsed fast-exit — no charge rows created
  if (!isExpanded) {
    return (
      <Box ref={groupRef} sx={wrapperSx}>
        <GroupHeader
          dateKey={props.dateKey}
          charges={props.charges}
          isMilestoneMode={props.isMilestoneMode}
          currencyCode={props.currencyCode}
          isExpanded={false}
          onToggle={handleExpand}
        />
      </Box>
    );
  }

  return (
    <Box ref={groupRef} sx={wrapperSx}>
      <GroupHeader
        dateKey={props.dateKey}
        charges={props.charges}
        isMilestoneMode={props.isMilestoneMode}
        currencyCode={props.currencyCode}
        isExpanded
        onToggle={handleCollapse}
      />
      <Box sx={{ pl: 2.5 }}>
        {props.charges.map((entry) => (
          <ChargeRowWithCallbacks
            key={entry.charge.id ?? `new-${String(entry.index)}`}
            charge={entry.charge}
            displayLabel={entry.displayLabel}
            currencyCode={props.currencyCode}
            chargeIndex={entry.index}
            isEditing={props.editingChargeIndex === entry.index}
            isDisabled={
              isAnotherEditing && props.editingChargeIndex !== entry.index
            }
            onStartEdit={props.onStartEdit}
            onStopEdit={props.onStopEdit}
            onUpdateCharge={props.onUpdateCharge}
            onDeleteCharge={props.onDeleteCharge}
            onSplitCharge={props.onSplitCharge}
          />
        ))}
      </Box>
    </Box>
  );
});

const GroupHeader = memo(function GroupHeader(props: {
  dateKey: string;
  charges: ChargeWithIndex[];
  isMilestoneMode: boolean;
  currencyCode: IsoCurrencyCode;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const groupTotal = useMemo(
    () => sumChargeTotals(props.charges.map((c) => c.charge)),
    [props.charges]
  );

  const dateLabel = useMemo(() => {
    if (props.dateKey === 'Unscheduled') return 'Unscheduled';
    const prefix = props.isMilestoneMode ? 'Expected bill date' : 'Bill date';
    return `${prefix}: ${DateTime.fromISO(props.dateKey).toFormat('LLL d, yyyy')}`;
  }, [props.dateKey, props.isMilestoneMode]);

  return (
    <ButtonBase
      onClick={props.onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        mb: 0.5,
        py: 0.25,
        borderRadius: 0.5,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <ExpandMoreRounded
          color="action"
          sx={{
            transform: props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms',
          }}
        />
        <CalendarTodayOutlined color="action" />
        <Typography variant="subtitle2">{dateLabel}</Typography>
      </Box>
      <Typography variant="subtitle2" color="text.secondary">
        {formatCurrencyValue({
          amount: groupTotal,
          isoCode: props.currencyCode,
        })}{' '}
        ({pluralize(props.charges.length, { singular: 'charge' })})
      </Typography>
    </ButtonBase>
  );
});

// Stabilizes callbacks per charge index. Receives pre-computed isEditing/isDisabled
// booleans so that editingChargeIndex changes don't defeat ChargeRow's memo for
// rows whose editing state didn't change.
const ChargeRowWithCallbacks = memo(function ChargeRowWithCallbacks(props: {
  charge: ChargeFormValue;
  displayLabel: string;
  currencyCode: IsoCurrencyCode;
  chargeIndex: number;
  isEditing: boolean;
  isDisabled: boolean;
  onStartEdit: (index: number) => void;
  onStopEdit: () => void;
  onUpdateCharge: (index: number, updated: ChargeFormValue) => void;
  onDeleteCharge: (index: number) => void;
  onSplitCharge: (index: number) => void;
}) {
  const chargeIndex = props.chargeIndex;
  const onUpdateCharge = props.onUpdateCharge;
  const onDeleteCharge = props.onDeleteCharge;
  const onSplitCharge = props.onSplitCharge;
  const onStartEdit = props.onStartEdit;

  const onUpdate = useCallback(
    (updated: ChargeFormValue) => onUpdateCharge(chargeIndex, updated),
    [onUpdateCharge, chargeIndex]
  );
  const onDelete = useCallback(
    () => onDeleteCharge(chargeIndex),
    [onDeleteCharge, chargeIndex]
  );
  const onSplit = useCallback(
    () => onSplitCharge(chargeIndex),
    [onSplitCharge, chargeIndex]
  );
  const handleStartEdit = useCallback(
    () => onStartEdit(chargeIndex),
    [onStartEdit, chargeIndex]
  );

  return (
    <ChargeRow
      charge={props.charge}
      displayLabel={props.displayLabel}
      currencyCode={props.currencyCode}
      isEditing={props.isEditing}
      isDisabled={props.isDisabled}
      onStartEdit={handleStartEdit}
      onStopEdit={props.onStopEdit}
      onUpdate={onUpdate}
      onDelete={onDelete}
      onSplit={onSplit}
    />
  );
});
